<template>
    <v-row>
        <v-col
            cols="12"
            sm="8"
            md="6"
            lg="6"
            xl="4"
            offset-sm="2"
            offset-md="3"
            offset-lg="3"
            offset-xl="4"
            class="mt-4"
        >
            <v-alert v-if="hasConfirmationTokenError" type="warning" prominent>
                <b>Upps! Der von Ihnen genutzte Link ist nicht länger gültig.</b>
                <br />
                <br />
                Bitte schauen Sie nach, ob in Ihrem E-Mail-Postfach eine neuere E-Mail mit Link
                vorhanden ist oder beantragen Sie nochmal ein neues Passwort.
                <v-btn
                    block
                    class="my-4"
                    color="primary"
                    variant="elevated"
                    :to="{ name: user ? 'PasswordChangeRequest' : 'PasswordReset' }"
                >
                    Passwortänderung beantragen
                </v-btn>
            </v-alert>
            <v-card v-else>
                <v-card-title class="text-center">
                    <h2>Passwort ändern</h2>
                </v-card-title>
                <v-card-text>
                    <v-form @submit.stop.prevent>
                        <v-text-field
                            v-model="password"
                            prepend-inner-icon="mdi-lock-outline"
                            :append-inner-icon="
                                isPasswordVisible ? 'mdi-eye-off-outline' : 'mdi-eye-outline'
                            "
                            :type="passwordFieldType"
                            label="Passwort"
                            placeholder="Passwort"
                            name="password"
                            data-test="password"
                            autocomplete="off"
                            :disabled="isPasswordChanged || isLoading"
                            density="compact"
                            variant="outlined"
                            color="primary"
                            base-color="primary"
                            clearable
                            counter
                            maxlength="40"
                            :rules="[rules.required, rules.counter]"
                            persistent-counter
                            @click:append-inner="switchPasswordVisibility"
                            @click:clear="password = ''"
                        />
                        <v-text-field
                            v-model="passwordRepeat"
                            prepend-inner-icon="mdi-lock-outline"
                            :append-inner-icon="
                                isPasswordVisible ? 'mdi-eye-off-outline' : 'mdi-eye-outline'
                            "
                            :type="passwordFieldType"
                            label="Passwort wiederholen"
                            placeholder="Passwort"
                            name="passwordRepeat"
                            data-test="passwordRepeat"
                            autocomplete="off"
                            :disabled="isPasswordChanged || isLoading"
                            density="compact"
                            variant="outlined"
                            color="primary"
                            base-color="primary"
                            clearable
                            counter
                            maxlength="40"
                            :rules="[rules.required, rules.counter]"
                            persistent-counter
                            @click:append-inner="switchPasswordVisibility"
                            @click:clear="passwordRepeat = ''"
                        />
                        <CommonPasswordStrength
                            class="my-1"
                            :password="password"
                            @update:strength="passwordStrength = $event"
                        />
                        <v-alert
                            v-if="false === passwordState && passwordInvalidText"
                            type="warning"
                            class="mb-3"
                        >
                            {{ passwordInvalidText }}
                        </v-alert>
                        <common-global-form-error :error="passwordErrors" is-html class="mb-3" />
                        <v-btn
                            :disabled="
                                !passwordState ||
                                isLoading ||
                                isPasswordChanged
                            "
                            block
                            color="secondary"
                            type="submit"
                            data-test="btn-change-password"
                            :loading="isLoading"
                            @click="changePassword()"
                        >
                            Passwort ändern
                        </v-btn>
                        <general-error-alert
                            v-if="hasError && !hasConfirmationTokenError && !hasPassWordChangeError"
                        />
                    </v-form>
                    <v-alert
                        v-if="isPasswordChanged && !hasError"
                        type="success"
                        class="mt-3"
                        prominent
                    >
                        <p class="font-weight-bold">Herzlichen Glückwunsch!</p>
                        <p>
                            Sie haben erfolgreich Ihr Passwort geändert.
                            <span v-if="!user">
                                <br />
                                <br />
                                Melden Sie sich jetzt an:
                            </span>
                        </p>
                        <v-btn v-if="!user" :to="{ name: 'Login' }" color="secondary" block>
                            Zur Anmeldung
                        </v-btn>
                    </v-alert>
                </v-card-text>
            </v-card>
        </v-col>
    </v-row>
</template>
<script>
    'use strict'
    import { FormError, GeneralErrorAlert } from '@/js/components/Common'
    import { getViolationsFeedback } from '@/js/utils'

    export default {
        name: 'RequestPasswordChange',
        components: {
            FormError,
            GeneralErrorAlert
        },
        props: {
            userId: {
                required: true,
                type: String
            },
            confirmationToken: {
                required: true,
                type: String
            }
        },
        data: () => ({
            password: '',
            passwordRepeat: '',
            passwordRepeatText: 'Die Passwörter sind unterschiedlich.',
            passwordRepeatHelp:
                'Bitte geben Sie zwei Mal das gleiche Passwort ein um Tippfehler zu vermeiden.',
            passwordState: false,
            passwordStrength: 0,
            passwordInvalidText: '',
            isPasswordChanged: false,
            passwordFieldType: 'password',
            rules: {
                required: (value) => !!value || 'Das Feld ist benötigt.',
                counter: (value) => value.length <= 40 || 'Maximal 40 Zeichen'
            }
        }),
        computed: {
            isLoading() {
                return this.$store.getters['security/isLoading']
            },
            hasError() {
                return this.$store.getters['security/hasError']
            },
            hasPassWordChangeError() {
                return this.$store.getters['security/hasPasswordChangeError']
            },
            error() {
                return this.$store.getters['security/error']
            },
            passwordErrors() {
                return getViolationsFeedback(['password'], this.error)
            },
            user() {
                return this.$store.getters['security/currentUser']
            },
            isPasswordVisible() {
                return 'text' === this.passwordFieldType
            },
            hasConfirmationTokenError() {
                return this.$store.getters['security/hasConfirmationTokenError']
            }
        },
        async created() {
            await this.$store.dispatch('security/isConfirmationTokenValid', {
                userId: this.userId,
                confirmationToken: this.confirmationToken
            })
        },
        methods: {
            passwordValidation() {
                const value = this.password
                const repeat = this.passwordRepeat

                let regex = new RegExp('^[\\w_.\+*,:;#!?=%&{}|$@()\\-\\[\\]\/\\\\]*$')
                if (value.length < 1) {
                    this.passwordState = null
                    this.passwordInvalidText = ''
                    return
                }
                if (!regex.test(value)) {
                    this.passwordInvalidText =
                        'Ihr Passwort darf nur aus Buchstaben, Ziffern und folgenden Sonderzeichen _.*-+:#!?%{}|@[];=&$\\/,() bestehen.'
                    this.passwordState = false
                    return
                }
                if (value.trim().length < 7 || value.trim().length > 40) {
                    this.passwordInvalidText =
                        'Ihr Passwort muss zwischen 7 und 40 Zeichen enthalten.'
                    this.passwordState = false
                    return
                }

                if (this.passwordStrength < 3) {
                    this.passwordInvalidText =
                        'Ihr Passwort ist zu schwach. Bitte verwenden Sie eine stärkere Kombination.'
                    this.passwordState = false
                    return
                }
                if (value.trim().length > 7 || repeat.trim().length > 7) {
                    if (value !== repeat) {
                        this.passwordInvalidText = this.passwordRepeatText
                        this.passwordState = false
                        return
                    }
                }

                this.passwordInvalidText = ''
                this.passwordState = true
            },
            async changePassword() {
                if ('text' === this.passwordFieldType) {
                    // ensure that password can be saved via browser
                    this.switchPasswordVisibility()
                }
                let result = await this.$store.dispatch('security/changePassword', {
                    userId: this.userId,
                    password: this.password,
                    confirmationToken: this.confirmationToken
                })
                if (result) {
                    this.isPasswordChanged = true
                }
            },
            switchPasswordVisibility() {
                this.passwordFieldType = 'text' === this.passwordFieldType ? 'password' : 'text'
            }
        },
        watch: {
            password: 'passwordValidation',
            passwordRepeat: 'passwordValidation',
            passwordStrength: 'passwordValidation'
        }
    }
</script>

<style scoped></style>
